import { Accordion, Alert, AlertIcon, Button, Flex } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { EventSeriesComplete, EventType, SeriesOverrideGroup } from '../../../../../services/Event/types'
import { SelectTeacher } from '../../../../../services/Teacher/types'
import { EditionCard } from './EditionCard'
import { EditionForm, SeriesForm } from './eventSeriesFormShared'

interface IProps {
  form: SeriesForm
  series?: EventSeriesComplete
  fieldErrors: Record<string, string[]>
  saving: boolean
  teachers: SelectTeacher[]
  eventTypes: EventType[]
  activeCampaignLists: { id: string; name: string }[]
  addEdition: () => void
  removeEdition: (key: string) => void
  patchEdition: (key: string, patch: Partial<EditionForm>) => void
  toggleOverride: (edition: EditionForm, group: SeriesOverrideGroup, enabled: boolean) => void
  editionNumber: (edition: EditionForm) => number
  targetSlug: (edition: EditionForm) => string
  willChangeSlug: (edition: EditionForm) => boolean
  editionLabel: (ed: EditionForm) => string
  editionAllowsHelpForm: (index: number) => boolean
  /** Expands (and scrolls to) this edition's card on mount — set from the "Editar
   *  edição" menu item on the "Ver edições" screen. */
  initialExpandedKey?: string
}

export function EditionsTab({
  form,
  series,
  fieldErrors,
  saving,
  teachers,
  eventTypes,
  activeCampaignLists,
  addEdition,
  removeEdition,
  patchEdition,
  toggleOverride,
  editionNumber,
  targetSlug,
  willChangeSlug,
  editionLabel,
  editionAllowsHelpForm,
  initialExpandedKey
}: IProps) {
  // Collapsed by default — a series with many editions would otherwise mount every
  // card's professionals/overrides at once. `allowMultiple` so more than one can be
  // compared side by side. Tracked by `_key` rather than position: editions are shown
  // newest-first (below) and a date edit can change that order while a card is open,
  // so "expanded" has to follow the edition, not whatever position it started at.
  // Seeded once from `initialExpandedKey` — safe as a lazy initializer because the
  // modal remounts fresh (new `key`) every time it's opened.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(initialExpandedKey ? [initialExpandedKey] : [])
  )
  const previousFieldErrors = useRef(fieldErrors)

  // Newest edition on top. Reuses `editionNumber` (start-date order, same tie-break as the
  // API) instead of re-sorting by date here, so this can never disagree with the number shown
  // elsewhere in the modal — it just walks the numbers highest to lowest.
  const displayOrder = form.editions
    .map((_, i) => i)
    .sort((a, b) => editionNumber(form.editions[b]) - editionNumber(form.editions[a]))

  const expandedPositions = displayOrder
    .map((originalIndex, position) => ({ position, key: form.editions[originalIndex]._key }))
    .filter(({ key }) => expandedKeys.has(key))
    .map(({ position }) => position)

  // After a failed save, auto-expand any edition that got an error — collapsed cards
  // would otherwise hide it (today everything is always open, so this keeps the same
  // "you can always see the error" guarantee instead of regressing it).
  useEffect(() => {
    if (fieldErrors === previousFieldErrors.current) return
    previousFieldErrors.current = fieldErrors

    const erroringKeys = form.editions
      .filter((_, i) => Object.keys(fieldErrors).some((key) => key.startsWith(`editions.${i}.`)))
      .map((ed) => ed._key)

    if (erroringKeys.length === 0) return

    setExpandedKeys((prev) => new Set([...Array.from(prev), ...erroringKeys]))
    // Only fieldErrors should trigger this — form.editions changes on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors])

  return (
    <>
      <Flex justifyContent="flex-end" mb={4}>
        <Button size="sm" colorScheme="magenta" variant="outline" onClick={addEdition} disabled={saving}>
          + Adicionar edição
        </Button>
      </Flex>

      {form.editions.length === 0 && (
        <Alert status="info" fontSize="sm" borderRadius="md" mb={4}>
          <AlertIcon />
          Esta série está sem edições. Você pode salvá-la assim e adicionar eventos depois pela lista de eventos avulsos
          (ação “Adicionar a uma série”), ou clicar em “Adicionar edição”.
        </Alert>
      )}

      <Accordion
        index={expandedPositions}
        onChange={(positions) => {
          const keys = (positions as number[]).map((position) => form.editions[displayOrder[position]]._key)
          setExpandedKeys(new Set(keys))
        }}
        allowMultiple
        display="flex"
        flexDirection="column"
        gap={3}
      >
        {/* Displayed newest edition first, but `index` (below) still points at the
        edition's real position in `form.editions` — that position is what field-error
        keys (`editions.{index}.`) and the save payload are keyed on, so it must not
        move just because the display order does. */}
        {displayOrder.map((i) => {
          const edition = form.editions[i]
          return (
            <EditionCard
              key={edition._key}
              edition={edition}
              index={i}
              // In edit mode, removing an existing edition deletes it on save, so keep the
              // original "more than one" guard; in create mode (no series yet) allow
              // removing down to zero to create an empty series.
              canRemove={!series || form.editions.length > 1}
              fieldErrors={fieldErrors}
              saving={saving}
              teachers={teachers}
              eventTypes={eventTypes}
              activeCampaignLists={activeCampaignLists}
              seriesFormat={form.format}
              removeEdition={removeEdition}
              patchEdition={patchEdition}
              toggleOverride={toggleOverride}
              // Computed here (not passed as functions) so EditionCard's React.memo can
              // compare plain values instead of always-new function references.
              targetSlugValue={targetSlug(edition)}
              willChangeSlugValue={willChangeSlug(edition)}
              editionLabelValue={editionLabel(edition)}
              helpFormAllowedValue={editionAllowsHelpForm(i)}
              focusOnMount={edition._key === initialExpandedKey}
            />
          )
        })}
      </Accordion>
    </>
  )
}
